// apps-layout.js - Centralized layout calculation system

export const LAYOUT_CONFIG = {
    // Easy configuration - adjust these to tune the layout
    outerMarginRatio: 0.5,        // Outer margins as ratio of icon size (0.5 = 50%)
    gapMinRatio: 0.1,             // Minimum gap as ratio of icon size
    gapMaxRatio: 0.8,             // Maximum gap as ratio of icon size
    regionGapMultiplier: 2,       // Region gaps are 2x icon gaps
    pageGapMultiplier: 2,         // Gap between pages during scroll
    
    // Folder-specific settings
    folderGapVerticalRatio: 0.15, // Fixed vertical gap for folders (15% of icon size)
    
    // Desktop defaults
    desktopMaxBaseIconSize: 120,
    desktopMinLeaves: 2,
    
    // Mobile defaults  
    mobileTargetLeaves: 1,
    mobileTargetBands: 2,
    
    // Slider settings
    sliderMin: 0.5,               // 50%
    sliderMax: 2.0,               // 200%
    sliderDefault: 1.0,           // 100%
    sliderStorageKey: 'zebby_icon_size_pref',
    
    // Folder settings
    folderExtraRankRatios: {
        1: 0.5,   // 1 grand rank used --> show 1.5 grand ranks
        2: 0.5,   // 2 grand ranks used --> show 2.5 grand ranks
        3: 0.5   // 3 grand ranks used --> show 3.25 grand ranks
    }
};

// Get user's icon size preference (50% to 200%)
export function getUserIconSizePreference() {
    const stored = localStorage.getItem(LAYOUT_CONFIG.sliderStorageKey);
    if (stored) {
        const value = parseFloat(stored);
        if (!isNaN(value) && value >= LAYOUT_CONFIG.sliderMin && value <= LAYOUT_CONFIG.sliderMax) {
            return value;
        }
    }
    return LAYOUT_CONFIG.sliderDefault;
}

// Save user's icon size preference
export function setUserIconSizePreference(percentage) {
    localStorage.setItem(LAYOUT_CONFIG.sliderStorageKey, percentage.toString());
}

// Calculate base icon size before user adjustment
function calculateBaseIconSize(viewportWidth, viewportHeight, isMobile) {
    if (isMobile) {
        // Mobile: Target 1 leaf by 2 bands
        const targetLeaves = LAYOUT_CONFIG.mobileTargetLeaves;
        const targetBands = LAYOUT_CONFIG.mobileTargetBands;
        
        // Each region is 4 icons wide by 3 icons tall
        const iconsWide = targetLeaves * 4;
        const iconsTall = targetBands * 3;
        
        // Reserve space for margins, gaps, and text
        const textHeight = 20; // Height reserved for text under each icon
        const marginRatio = LAYOUT_CONFIG.outerMarginRatio;
        const gapRatio = (LAYOUT_CONFIG.gapMinRatio + LAYOUT_CONFIG.gapMaxRatio) / 2; // Use average
        
        // Calculate from width (limiting factor on mobile usually)
        // width = 2*margin + icons*iconSize + (icons-1)*gap + (regions-1)*regionGap
        // regionGap = 2*gap, so: width = 2*margin + icons*iconSize + gaps_total
        // where gaps_total = (icons-1)*gap + (regions-1)*2*gap
        const iconGapsBetween = iconsWide - 1;
        const regionGapsBetween = targetLeaves - 1;
        
        // Solve for iconSize:
        // width = 2*marginRatio*iconSize + iconsWide*iconSize + iconGapsBetween*gapRatio*iconSize + regionGapsBetween*2*gapRatio*iconSize
        const widthCoefficient = (2 * marginRatio) + iconsWide + 
            (iconGapsBetween * gapRatio) + 
            (regionGapsBetween * 2 * gapRatio);
        const iconSizeFromWidth = viewportWidth / widthCoefficient;
        
        // Calculate from height
        // height = 2*margin + iconsTall*(iconSize+text) + (iconsTall-1)*gap + (regions-1)*regionGap
        const heightCoefficient = (2 * marginRatio) + iconsTall + 
            (iconsTall - 1) * gapRatio + 
            (targetBands - 1) * 2 * gapRatio;
        const iconSizeFromHeight = (viewportHeight - (iconsTall * textHeight)) / heightCoefficient;
        
        // Use the smaller of the two (limiting factor)
        return Math.floor(Math.min(iconSizeFromWidth, iconSizeFromHeight));
    } else {
        // Desktop: Start with max size and fit as many leaves as possible, min 2 leaves
        const maxIconSize = LAYOUT_CONFIG.desktopMaxBaseIconSize;
        const minLeaves = LAYOUT_CONFIG.desktopMinLeaves;
        
        // Try to fit leaves at max icon size
        // Use average gap ratio for estimation
        const gapRatio = (LAYOUT_CONFIG.gapMinRatio + LAYOUT_CONFIG.gapMaxRatio) / 2;
        const marginRatio = LAYOUT_CONFIG.outerMarginRatio;
        
        // Calculate how many leaves fit at max icon size
        // Each region is 4 icons wide
        const iconsPerRegion = 4;
        
        // Test progressively more leaves starting from minLeaves
        for (let leaves = minLeaves; leaves <= 6; leaves++) {
            const iconsWide = leaves * iconsPerRegion;
            const iconGapsBetween = iconsWide - 1;
            const regionGapsBetween = leaves - 1;
            
            const widthNeeded = (2 * marginRatio * maxIconSize) + 
                (iconsWide * maxIconSize) +
                (iconGapsBetween * gapRatio * maxIconSize) +
                (regionGapsBetween * 2 * gapRatio * maxIconSize);
            
            if (widthNeeded > viewportWidth) {
                // This leaf count doesn't fit, use previous
                const finalLeaves = Math.max(minLeaves, leaves - 1);
                
                // Recalculate optimal icon size for this leaf count
                const finalIconsWide = finalLeaves * iconsPerRegion;
                const finalIconGaps = finalIconsWide - 1;
                const finalRegionGaps = finalLeaves - 1;
                
                const widthCoefficient = (2 * marginRatio) + finalIconsWide + 
                    (finalIconGaps * gapRatio) + 
                    (finalRegionGaps * 2 * gapRatio);
                
                const calculatedSize = viewportWidth / widthCoefficient;
                return Math.floor(Math.min(calculatedSize, maxIconSize));
            }
        }
        
        // If we can fit 6+ leaves at max size, use max size
        return maxIconSize;
    }
}

// Calculate the maximum icon size that still fits at least one region
export function calculateMaximumFeasibleIconSize(viewportWidth, viewportHeight) {
    const textHeight = 20;
    const marginRatio = LAYOUT_CONFIG.outerMarginRatio;
    const minGapRatio = LAYOUT_CONFIG.gapMinRatio;
    
    // One region = 4 icons wide Ãƒâ€” 3 icons tall
    // Solve for maximum icon size that fits one region
    // width = 2*margin + 4*iconSize + 3*gap
    // where margin = marginRatio * iconSize and gap = minGapRatio * iconSize
    const widthCoefficient = (2 * marginRatio) + 4 + (3 * minGapRatio);
    const maxIconSizeFromWidth = viewportWidth / widthCoefficient;
    
    // height = 2*margin + 3*(iconSize + textHeight) + 2*gap
    const heightCoefficient = (2 * marginRatio) + 3 + (2 * minGapRatio);
    const maxIconSizeFromHeight = (viewportHeight - (3 * textHeight)) / heightCoefficient;
    
    const maxFeasibleIconSize = Math.floor(Math.min(maxIconSizeFromWidth, maxIconSizeFromHeight));
    
    return maxFeasibleIconSize;
}

// Calculate the maximum slider percentage based on viewport constraints
export function calculateMaximumSliderPercentage(viewportWidth, viewportHeight) {
    const isMobile = window.innerWidth <= 768;
    const baseIconSize = calculateBaseIconSize(viewportWidth, viewportHeight, isMobile);
    const maxFeasibleIconSize = calculateMaximumFeasibleIconSize(viewportWidth, viewportHeight);
    
    // Calculate what percentage the max feasible size represents
    const maxPercentage = maxFeasibleIconSize / baseIconSize;
    
    // Clamp to reasonable bounds (at least match LAYOUT_CONFIG.sliderMax, but could be less)
    return Math.min(maxPercentage, LAYOUT_CONFIG.sliderMax);
}

// Apply user's slider adjustment to base icon size
function applyUserIconSizeAdjustment(baseSize, userPercentage) {
    return Math.floor(baseSize * userPercentage);
}

// Calculate how many leaves and bands fit with given icon size
function calculateLayoutGrid(iconSize, viewportWidth, viewportHeight, isMobile) {
    const textHeight = 20;
    const marginRatio = LAYOUT_CONFIG.outerMarginRatio;
    const minGapRatio = LAYOUT_CONFIG.gapMinRatio;
    const maxGapRatio = LAYOUT_CONFIG.gapMaxRatio;
    
    // Calculate margins
    const margin = iconSize * marginRatio;
    const availableWidth = viewportWidth - (2 * margin);
    const availableHeight = viewportHeight - (2 * margin);
    
    // Try to fit leaves (region columns)
    let bestLeaves = 1;
    const minLeaves = isMobile ? 1 : LAYOUT_CONFIG.desktopMinLeaves;
    
    for (let leaves = minLeaves; leaves <= 6; leaves++) {
        const iconsWide = leaves * 4;
        const iconGapsBetween = iconsWide - 1;
        const regionGapsBetween = leaves - 1;
        
        // Calculate with minimum gaps first
        const minGap = iconSize * minGapRatio;
        const minRegionGap = minGap * LAYOUT_CONFIG.regionGapMultiplier;
        
        const widthNeeded = (iconsWide * iconSize) + 
            (iconGapsBetween * minGap) + 
            (regionGapsBetween * minRegionGap);
        
        if (widthNeeded <= availableWidth) {
            bestLeaves = leaves;
        } else {
            break;
        }
    }
    
    // Try to fit bands (region rows) - on mobile we want at least 2 bands if possible
    let bestBands = 1;
    const targetBands = isMobile ? LAYOUT_CONFIG.mobileTargetBands : 1;
    
    for (let bands = targetBands; bands <= 4; bands++) {
        const iconsTall = bands * 3;
        const iconGapsBetween = iconsTall - 1;
        const regionGapsBetween = bands - 1;
        
        const minGap = iconSize * minGapRatio;
        const minRegionGap = minGap * LAYOUT_CONFIG.regionGapMultiplier;
        
        const heightNeeded = (iconsTall * (iconSize + textHeight)) + 
            (iconGapsBetween * minGap) + 
            (regionGapsBetween * minRegionGap);
        
        if (heightNeeded <= availableHeight) {
            bestBands = bands;
        } else {
            break;
        }
    }
    
    return { leaves: bestLeaves, bands: bestBands };
}

// Calculate actual gaps to center content and fill space nicely
function calculateOptimalGaps(iconSize, leaves, bands, viewportWidth, viewportHeight) {
    const textHeight = 20;
    const marginRatio = LAYOUT_CONFIG.outerMarginRatio;
    const minGapRatio = LAYOUT_CONFIG.gapMinRatio;
    const maxGapRatio = LAYOUT_CONFIG.gapMaxRatio;
    
    const margin = iconSize * marginRatio;
    const minGap = iconSize * minGapRatio;
    const maxGap = iconSize * maxGapRatio;
    
    const iconsWide = leaves * 4;
    const iconsTall = bands * 3;
    
    // Calculate horizontal gap
    const availableWidthForGaps = viewportWidth - (2 * margin) - (iconsWide * iconSize);
    const iconGapCountH = iconsWide - 1;
    const regionGapCountH = leaves - 1;
    
    // regionGap = 2 * iconGap, so:
    // availableWidth = iconGapCountH * iconGap + regionGapCountH * 2 * iconGap
    // availableWidth = iconGap * (iconGapCountH + 2 * regionGapCountH)
    const totalGapUnitsH = iconGapCountH + (LAYOUT_CONFIG.regionGapMultiplier * regionGapCountH);
    let gapH = availableWidthForGaps / totalGapUnitsH;
    gapH = Math.max(minGap, Math.min(maxGap, gapH));
    
    // Calculate vertical gap
    const availableHeightForGaps = viewportHeight - (2 * margin) - (iconsTall * (iconSize + textHeight));
    const iconGapCountV = iconsTall - 1;
    const regionGapCountV = bands - 1;
    
    const totalGapUnitsV = iconGapCountV + (LAYOUT_CONFIG.regionGapMultiplier * regionGapCountV);
    let gapV = availableHeightForGaps / totalGapUnitsV;
    gapV = Math.max(minGap, Math.min(maxGap, gapV));
    
    return {
        gapH: Math.floor(gapH),
        gapV: Math.floor(gapV),
        margin: Math.floor(margin)
    };
}

// Check if a single region can fit with current icon size
function canFitOneRegion(iconSize, viewportWidth, viewportHeight) {
    const textHeight = 20;
    const marginRatio = LAYOUT_CONFIG.outerMarginRatio;
    const minGapRatio = LAYOUT_CONFIG.gapMinRatio;
    
    const margin = iconSize * marginRatio;
    const minGap = iconSize * minGapRatio;
    
    // One region = 4 icons wide Ãƒâ€” 3 icons tall
    const widthNeeded = (2 * margin) + (4 * iconSize) + (3 * minGap);
    const heightNeeded = (2 * margin) + (3 * (iconSize + textHeight)) + (2 * minGap);
    
    return widthNeeded <= viewportWidth && heightNeeded <= viewportHeight;
}

// Cap icon size if it's too large to fit even one region
function capIconSizeForMinimumLayout(iconSize, viewportWidth, viewportHeight) {
    const textHeight = 20;
    const marginRatio = LAYOUT_CONFIG.outerMarginRatio;
    const minGapRatio = LAYOUT_CONFIG.gapMinRatio;
    
    // Solve for maximum icon size that fits one region
    // width = 2*margin + 4*iconSize + 3*gap
    // where margin = marginRatio * iconSize and gap = minGapRatio * iconSize
    const widthCoefficient = (2 * marginRatio) + 4 + (3 * minGapRatio);
    const maxIconSizeFromWidth = viewportWidth / widthCoefficient;
    
    // height = 2*margin + 3*(iconSize + textHeight) + 2*gap
    const heightCoefficient = (2 * marginRatio) + 3 + (2 * minGapRatio);
    const maxIconSizeFromHeight = (viewportHeight - (3 * textHeight)) / heightCoefficient;
    
    const maxIconSize = Math.floor(Math.min(maxIconSizeFromWidth, maxIconSizeFromHeight));
    
    return Math.min(iconSize, maxIconSize);
}

// Main layout calculation function
export function calculateCompleteLayout(viewportWidth, viewportHeight) {
    const isMobile = window.innerWidth <= 768;
    
    // Phase 1: Calculate base icon size
    let baseIconSize = calculateBaseIconSize(viewportWidth, viewportHeight, isMobile);
    
    // Phase 2: Apply user adjustment
    const userPercentage = getUserIconSizePreference();
    
    // Calculate dynamic max and clamp user preference
    const maxPercentage = calculateMaximumSliderPercentage(viewportWidth, viewportHeight);
    const clampedUserPercentage = Math.min(userPercentage, maxPercentage);
    
    let iconSize = applyUserIconSizeAdjustment(baseIconSize, clampedUserPercentage);
    
    // Phase 3: Calculate layout grid
    let { leaves, bands } = calculateLayoutGrid(iconSize, viewportWidth, viewportHeight, isMobile);
    
    // Phase 4: Check if we need to cap icon size
    if (!canFitOneRegion(iconSize, viewportWidth, viewportHeight)) {
        console.warn('Icon size too large, capping to fit minimum layout');
        iconSize = capIconSizeForMinimumLayout(iconSize, viewportWidth, viewportHeight);
        // Recalculate grid with capped size
        ({ leaves, bands } = calculateLayoutGrid(iconSize, viewportWidth, viewportHeight, isMobile));
    }
    
    // Phase 5: Calculate optimal gaps and finalize
    const { gapH, gapV, margin } = calculateOptimalGaps(iconSize, leaves, bands, viewportWidth, viewportHeight);
    
    // Calculate derived values
    const regionWidth = (4 * iconSize) + (3 * gapH);
    const regionHeight = (3 * (iconSize + 20)) + (2 * gapV);
    const regionGapH = gapH * LAYOUT_CONFIG.regionGapMultiplier;
    const regionGapV = gapV * LAYOUT_CONFIG.regionGapMultiplier;
    const titleMaxWidth = iconSize + (gapH * 0.8);
    
    return {
        iconSize,
        baseIconSize,
        userPercentage,
        gapH,
        gapV,
        margin,
        regionGapH,
        regionGapV,
        leaves,
        bands,
        regionWidth,
        regionHeight,
        titleMaxWidth,
        isMobile
    };
}

export function calculateFolderDimensions(layout, maxUsedGrandRank) {
    // maxUsedGrandRank is 0-indexed (0, 1, or 2)
    // Grand rank: a rank spanning all pages of a folder
    const usedGrandRanks = maxUsedGrandRank + 1;
    const extraRatio = LAYOUT_CONFIG.folderExtraRankRatios[usedGrandRanks] || 0.25;
    const totalRanks = usedGrandRanks + extraRatio;
    
    const textHeight = 20;
    
    // Use fixed vertical gap for folders (more compact)
    const folderGapV = Math.floor(layout.iconSize * LAYOUT_CONFIG.folderGapVerticalRatio);
    
    // Calculate folder width to match main screen CONTENT area
    const contentWidth = (2 * layout.margin) + 
        (layout.leaves * layout.regionWidth) + 
        ((layout.leaves - 1) * layout.regionGapH);
    
    // Calculate folder height based on icon ranks
    const iconsHeight = totalRanks * (layout.iconSize + textHeight);
    const gapsHeight = (usedGrandRanks - 1) * folderGapV;
    const contentHeight = iconsHeight + gapsHeight;
    
    // Only add top margin - bottom should be flush or minimal
    const folderHeight = layout.margin + contentHeight;
    
    return {
        width: Math.floor(contentWidth),
        height: Math.floor(folderHeight),
        contentHeight: Math.floor(contentHeight),
        gapV: folderGapV
    };
}

// ===== COORDINATE SYSTEM UTILITIES =====
// These functions help convert between visual (rank/file) coordinates and region/position coordinates
// Visual coordinates: rank and file in the visible grid (0-indexed)
// Region/position: the underlying data storage format

export function isRankOccupied(rank, icons, layout, isFolder, currentScreen) {
    // Check all files in this rank ON THE CURRENT SCREEN ONLY
    const totalFiles = layout.leaves * 4;
    
    // Calculate which absolute ranks are on the current screen
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    
    // Calculate the first rank on this screen
    const screenFirstRank = isFolder ? 0 : Math.floor((currentScreen * regionsPerScreen) / layout.leaves) * 3;
    
    // Convert to absolute rank
    const absoluteRank = screenFirstRank + rank;
    
    // Import render dynamically to avoid circular dependency
    const render = window.appsRender;
    if (!render) return false;
    
    for (let file = 0; file < totalFiles; file++) {
        const slotInfo = render.visualPositionToRegionPosition(absoluteRank, file, layout);
        if (!slotInfo) continue;
        
        // Check if there's an icon at this slot
        if (icons.some(icon => icon.region === slotInfo.region && icon.position === slotInfo.position)) {
            return true;
        }
    }
    return false;
}

export function isFileOccupied(file, icons, layout, isFolder, currentScreen) {
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const ranksPerScreen = isFolder ? 3 : layout.bands * 3;
    
    // Calculate the first rank on this screen
    const screenFirstRank = isFolder ? 0 : Math.floor((currentScreen * regionsPerScreen) / layout.leaves) * 3;
    
    // Import render dynamically to avoid circular dependency
    const render = window.appsRender;
    if (!render) return false;
    
    for (let rank = 0; rank < ranksPerScreen; rank++) {
        const absoluteRank = screenFirstRank + rank;
        const slotInfo = render.visualPositionToRegionPosition(absoluteRank, file, layout);
        if (!slotInfo) continue;
        
        // Check if there's an icon at this slot
        if (icons.some(icon => icon.region === slotInfo.region && icon.position === slotInfo.position)) {
            return true;
        }
    }
    return false;
}

export function getIconAtRankFile(rank, file, icons, layout, isFolder, currentScreen) {
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    
    // Calculate the first visual rank on this screen
    const screenFirstVisualRank = isFolder ? 0 : Math.floor((currentScreen * regionsPerScreen) / layout.leaves) * 3;
    
    // Convert screen-local rank to absolute visual rank
    const absoluteVisualRank = screenFirstVisualRank + rank;
    
    // Import render dynamically to avoid circular dependency
    const render = window.appsRender;
    if (!render) return null;
    
    // Use the correct helper to convert visual coords to region/position
    const slotInfo = render.visualPositionToRegionPosition(absoluteVisualRank, file, layout);
    
    if (!slotInfo) {
        return null;
    }
    
    // Find icon at this region and position
    return icons.find(icon => 
        icon.region === slotInfo.region && 
        icon.position === slotInfo.position
    );
}

export function getIconsInRank(rank, icons, layout, isFolder, currentScreen) {
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    const screenEndRegion = screenStartRegion + regionsPerScreen - 1;
    
    // Calculate the first visual rank on this screen
    const screenFirstVisualRank = isFolder ? 0 : Math.floor((currentScreen * regionsPerScreen) / layout.leaves) * 3;
    
    // Convert screen-local rank to absolute visual rank
    const absoluteVisualRank = screenFirstVisualank + rank;
    
    // Import render dynamically to avoid circular dependency
    const render = window.appsRender;
    if (!render) return [];
    
    const rankIcons = [];
    const totalVisualFiles = layout.leaves * 4;
    
    for (let file = 0; file < totalVisualFiles; file++) {
        const slotInfo = render.visualPositionToRegionPosition(absoluteVisualRank, file, layout);
        if (!slotInfo) continue;
        
        if (slotInfo.region < screenStartRegion || slotInfo.region > screenEndRegion) {
            continue;
        }
        
        const icon = icons.find(icon => 
            icon.region === slotInfo.region && 
            icon.position === slotInfo.position
        );
        
        if (icon) {
            rankIcons.push({ icon, file, region: slotInfo.region, position: slotInfo.position });
        }
    }
    
    return rankIcons.sort((a, b) => a.file - b.file);
}

export function getMaxOccupiedFile(icons, layout, isFolder, currentScreen) {
    const totalVisualFiles = layout.leaves * 4;
    
    let maxFile = -1;
    
    for (let file = 0; file < totalVisualFiles; file++) {
        if (isFileOccupied(file, icons, layout, isFolder, currentScreen)) {
            maxFile = file;
        }
    }
    
    return maxFile;
}

export function getMinOccupiedFile(icons, layout, isFolder, currentScreen) {
    const totalVisualFiles = layout.leaves * 4;
    
    for (let file = 0; file < totalVisualFiles; file++) {
        if (isFileOccupied(file, icons, layout, isFolder, currentScreen)) {
            return file;
        }
    }
    
    return -1;
}

export function getMinOccupiedRank(icons, layout, isFolder, currentScreen) {
    const totalVisualRanks = isFolder ? 3 : layout.bands * 3;
    
    for (let rank = 0; rank < totalVisualRanks; rank++) {
        if (isRankOccupied(rank, icons, layout, isFolder, currentScreen)) {
            return rank;
        }
    }
    
    return -1;
}

export function getMaxOccupiedRank(icons, layout, isFolder, currentScreen) {
    const totalVisualRanks = isFolder ? 3 : layout.bands * 3;
    
    let maxRank = -1;
    
    for (let rank = 0; rank < totalVisualRanks; rank++) {
        if (isRankOccupied(rank, icons, layout, isFolder, currentScreen)) {
            maxRank = rank;
        }
    }
    
    return maxRank;
}

export function getLeftmostIconInRank(rank, icons, layout, isFolder, currentScreen) {
    const totalVisualFiles = layout.leaves * 4;
    
    for (let file = 0; file < totalVisualFiles; file++) {
        const icon = getIconAtRankFile(rank, file, icons, layout, isFolder, currentScreen);
        if (icon) {
            return { icon, rank, file };
        }
    }
    return null;
}

export function getRightmostIconInRank(rank, icons, layout, isFolder, currentScreen) {
    const totalVisualFiles = layout.leaves * 4;
    
    for (let file = totalVisualFiles - 1; file >= 0; file--) {
        const icon = getIconAtRankFile(rank, file, icons, layout, isFolder, currentScreen);
        if (icon) {
            return { icon, rank, file };
        }
    }
    return null;
}

export function getTopmostIconInFile(file, icons, layout, isFolder, currentScreen) {
    const totalVisualRanks = isFolder ? 3 : layout.bands * 3;
    
    for (let rank = 0; rank < totalVisualRanks; rank++) {
        const icon = getIconAtRankFile(rank, file, icons, layout, isFolder, currentScreen);
        if (icon) {
            return { icon, rank, file };
        }
    }
    return null;
}
